import { ENDPOINTS, WEBVIEW_EVENTS } from '@/lib/consts.js'
import { PERMISSIONS, READERS, CONNECTIONS_STATUSES } from '@/components/settings/payments/payment-types/booksy-card-reader/consts.js'

import { useCore } from '@/composables/core/index.js'
import { useDevLogger } from '@/composables/dev-logger/index.js'

import { useBluetoothLocationAccess } from '@/components/settings/payments/payment-types/booksy-card-reader/permissions/bluetooth-location-access.js'
import { useWebViewEventBus } from '@/composables/web-view-event-bus/index.js'

import { ref, reactive } from 'vue'

let webViewEventBus

const CONNECT_TIMEOUT_SECONDS = 20

export async function createStripeWebview ({ getConnectionToken, handleUnexpectedTerminalDisconnect }) {
  if (webViewEventBus) throw new Error('Booksy Card Reader - webView was already called!')

  const { store, apiCall } = useCore()
  const bluetoothConnectionStatus = ref(CONNECTIONS_STATUSES.NOT_CONNECTED)
  webViewEventBus = useWebViewEventBus()

  const { checkAccess } = useBluetoothLocationAccess()

  const logger = useDevLogger('stripe-webView')

  const isNativeAppSupportingM2 = ref(false)

  await checkIsNativeAppSupportingM2()

  if (!isNativeAppSupportingM2.value) throw new Error('M2 not supported, native app update required!')

  webViewEventBus.addEventListener(WEBVIEW_EVENTS.BCR_CONNECTION_TOKEN, handleBluetoothConnectionToken)
  webViewEventBus.addEventListener(WEBVIEW_EVENTS.BCR_UNEXPECTED_DISCONNECT, handleUnexpectedTerminalDisconnect)

  async function handleBluetoothConnectionToken () {
    const connection_token = await getConnectionToken()
    webViewEventBus.sendEvent(WEBVIEW_EVENTS.BCR_CONNECTION_TOKEN, { connection_token })
  }

  function destroyService () {
    webViewEventBus.removeEventListener(WEBVIEW_EVENTS.BCR_CONNECTION_TOKEN, handleBluetoothConnectionToken)
    webViewEventBus.removeEventListener(WEBVIEW_EVENTS.BCR_UNEXPECTED_DISCONNECT, handleUnexpectedTerminalDisconnect)

    webViewEventBus = null
    readers.value = []
    isNativeAppSupportingM2.value = false
    resetUpdateState()
  }

  function resetUpdateState () {
    updateStatus.pending = false
    updateStatus.progress = 0
    updateStatus.succeed = null
    updateStatus.error = ''
  }

  /*
    CHECK BLUETOOTH SUPPORT
  */

  async function checkIsNativeAppSupportingM2 () {
    const isTablet = store.getters['app/isTablet']
    if (!isTablet) {
      isNativeAppSupportingM2.value = false
      return
    }
    try {
      await webViewEventBus.sendEventWithCallback(WEBVIEW_EVENTS.BLUETOOTH_ENABLED, {}, 1000)
      // just need response from native app from any new webview event
      isNativeAppSupportingM2.value = true
    } catch (error) {
      isNativeAppSupportingM2.value = false
      logger.error('M2 not supported, native app update required!')
    }
  }

  /*
    DISCOVER READERS
  */

  const readers = ref([])

  function handleBluetoothDiscoverReadersList ({ readers: newReaders }) {
    readers.value = newReaders.map(reader => {
      return {
        ...reader,
        isBluetooth: true,
      }
    })
  }

  function bluetoothDiscoverReadersStart () {
    readers.value = []
    webViewEventBus.addEventListener(WEBVIEW_EVENTS.BCR_DISCOVER_LIST, handleBluetoothDiscoverReadersList)
    webViewEventBus.sendEvent(WEBVIEW_EVENTS.BCR_DISCOVER_START)
  }

  function bluetoothDiscoverReadersCancel () {
    webViewEventBus.sendEvent(WEBVIEW_EVENTS.BCR_DISCOVER_CANCEL)
    webViewEventBus.removeEventListener(WEBVIEW_EVENTS.BCR_DISCOVER_LIST, handleBluetoothDiscoverReadersList)
  }

  /*
    CONNECT
  */

  function connect (reader) {
    const serialNumber = reader.serial_number
    if (!serialNumber) logger.error('No serial number provided!')

    const location = store.getters['business/stripeAccountLocation']
    if (!location) logger.error('No location provided!')

    return new Promise((resolve) => {
      const connectTimeout = setTimeout(() => {
        const connectResult = {
          error: {
            message: 'Connect timeout',
            code: 'BOOKSY_CODE_CONNECT_TIMEOUT',
          },
        }
        resolve(connectResult)
      }, CONNECT_TIMEOUT_SECONDS * 1000)

      function onUpdateStart () {
        clearTimeout(connectTimeout)
      }

      const onConnect = (connectResult) => {
        clearTimeout(connectTimeout)
        resolve(connectResult)
      }

      webViewEventBus.addEventListener(WEBVIEW_EVENTS.BCR_CONNECTION_STATUS_CHANGE, handleBluetoothConnectionStatusChange)
      webViewEventBus.addEventListener(WEBVIEW_EVENTS.BCR_UPDATE_START, handleBluetoothUpdateStart)
      webViewEventBus.addEventListener(WEBVIEW_EVENTS.BCR_UPDATE_START, onUpdateStart, { once: true })
      webViewEventBus.addEventListener(WEBVIEW_EVENTS.BCR_CONNECT, onConnect, { once: true })
      webViewEventBus.sendEvent(WEBVIEW_EVENTS.BCR_CONNECT, {
        serial_number: serialNumber,
        location_id: location,
      })
    })
  }

  function handleBluetoothConnectionStatusChange ({ status }) {
    bluetoothConnectionStatus.value = status
  }

  /*
    UPDATE
  */

  const updateStatus = reactive({
    pending: false,
    progress: 0,
    succeed: null,
    error: '',
  })

  function handleBluetoothUpdateStart () {
    webViewEventBus.addEventListener(WEBVIEW_EVENTS.BCR_UPDATE_STATUS, handleBluetoothUpdateStatus)
    webViewEventBus.addEventListener(WEBVIEW_EVENTS.BCR_UPDATE_DONE, handleBluetoothUpdateDone, { once: true })
    updateStatus.pending = true
    // TODO - check if updateStatus needs resetting
  }

  function handleBluetoothUpdateStatus ({ percentage }) {
    updateStatus.progress = percentage
  }

  async function handleBluetoothUpdateDone ({ succeed, error_description }) {
    webViewEventBus.removeEventListener(WEBVIEW_EVENTS.BCR_UPDATE_STATUS, handleBluetoothUpdateStatus)

    updateStatus.pending = false
    updateStatus.succeed = succeed
    if (!succeed) {
      updateStatus.error = error_description
      // this one time we want to behave like reader is not connected to app and show a connection error
      await disconnect()
    }
  }

  async function disconnect () {
    webViewEventBus.removeEventListener(WEBVIEW_EVENTS.BCR_CONNECTION_STATUS_CHANGE, handleBluetoothConnectionStatusChange)
    webViewEventBus.removeEventListener(WEBVIEW_EVENTS.BCR_UPDATE_START, handleBluetoothUpdateStart)
    await webViewEventBus.sendEventWithCallback(WEBVIEW_EVENTS.BCR_DISCONNECT)
  }

  async function checkPermissions () {
    const noAccess = (permission) => !permission.accessGranted || permission.accessDeniedPermanently || !permission.enabled

    const location = await checkAccess(PERMISSIONS.LOCATION)
    const bluetooth = await checkAccess(PERMISSIONS.BLUETOOTH)

    if (noAccess(location) || noAccess(bluetooth)) {
      throw Error('No granted permissions')
    }
  }

  function isBluetoothReader (device_type) {
    const reader = Object.values(READERS).find(reader => reader.device_type === device_type)
    return reader?.bluetooth
  }

  /*
    SET BLUETOOTH READER LABEL
  */

  const businessId = store.getters['business/businessId']

  async function setReaderLabel (serialNumber, label, id) {
    if (serialNumber.includes('SIMULATOR')) {
      return
    }

    await apiCall({
      method: 'PUT',
      url: ENDPOINTS.STRIPE_READER_LABEL,
      urlParams: {
        businessId,
        readerId: id,
      },
      data: { label },
    })
  }

  return {
    bluetoothDiscoverReadersStart,
    bluetoothDiscoverReadersCancel,
    connect,
    disconnect,
    checkPermissions,
    isBluetoothReader,
    setReaderLabel,
    destroyService,
    updateStatus,
    resetUpdateState,
    bluetoothConnectionStatus,
    readers,
  }
}
